import { useEffect, useCallback, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ChevronLeft, ChevronRight, Download, Loader2, Pencil, Check, X } from "lucide-react";
import type { ClaimPhoto } from "@/api/generated/client/models";

interface PhotoLightboxProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    photos: ClaimPhoto[];
    activeIndex: number;
    onNavigate: (index: number) => void;
    blobUrl: string | null;
    isLoading: boolean;
    error: string | null;
    onDownload: (photo: ClaimPhoto) => void;
    canWrite?: boolean;
    onSaveCaption?: (photo: ClaimPhoto, caption: string | null) => void | Promise<void>;
    isSaving?: boolean;
}

function isVideo(mimeType: string): boolean {
    return mimeType.startsWith("video/");
}

export function PhotoLightbox({
    open,
    onOpenChange,
    photos,
    activeIndex,
    onNavigate,
    blobUrl,
    isLoading,
    error,
    onDownload,
    canWrite = false,
    onSaveCaption,
    isSaving = false,
}: PhotoLightboxProps) {
    const { toast } = useToast();
    const activePhoto = photos[activeIndex];
    const hasPrev = activeIndex > 0;
    const hasNext = activeIndex < photos.length - 1;

    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState("");

    // Reset any in-progress caption edit when the active photo changes or the dialog closes.
    useEffect(() => {
        setIsEditing(false);
        setDraft("");
    }, [activeIndex, open]);

    const goPrev = useCallback(() => {
        if (hasPrev) onNavigate(activeIndex - 1);
    }, [hasPrev, activeIndex, onNavigate]);

    const goNext = useCallback(() => {
        if (hasNext) onNavigate(activeIndex + 1);
    }, [hasNext, activeIndex, onNavigate]);

    useEffect(() => {
        if (!open || isEditing) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") goPrev();
            else if (e.key === "ArrowRight") goNext();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, isEditing, goPrev, goNext]);

    const startEditing = useCallback(() => {
        if (!activePhoto) return;
        setDraft(activePhoto.caption ?? "");
        setIsEditing(true);
    }, [activePhoto]);

    const cancelEditing = useCallback(() => {
        setIsEditing(false);
        setDraft("");
    }, []);

    const saveCaption = useCallback(async () => {
        if (!activePhoto || !onSaveCaption) return;
        const trimmed = draft.trim();
        const next = trimmed.length > 0 ? trimmed : null;
        if (next === (activePhoto.caption ?? null)) {
            setIsEditing(false);
            return;
        }
        try {
            await onSaveCaption(activePhoto, next);
            setIsEditing(false);
        } catch {
            toast({
                title: "Failed to save caption",
                description: "Please try again.",
                variant: "destructive",
            });
        }
    }, [activePhoto, draft, onSaveCaption, toast]);

    if (!activePhoto) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl w-[95vw] p-0 gap-0 bg-background/95 backdrop-blur">
                <VisuallyHidden>
                    <DialogTitle>{activePhoto.filename}</DialogTitle>
                </VisuallyHidden>
                <div className="relative flex flex-col">
                    {/* Header */}
                    <div className="flex items-start justify-between px-4 py-2.5 pr-10 border-b min-w-0 gap-2">
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{activePhoto.filename}</p>
                            {isEditing ? (
                                <div className="mt-1 flex items-start gap-1">
                                    <Textarea
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                                e.preventDefault();
                                                void saveCaption();
                                            } else if (e.key === "Escape") {
                                                e.preventDefault();
                                                cancelEditing();
                                            }
                                        }}
                                        placeholder="Add a caption…"
                                        maxLength={1000}
                                        rows={2}
                                        autoFocus
                                        disabled={isSaving}
                                        className="min-h-0 resize-none text-xs"
                                        data-testid="caption-input"
                                    />
                                    <div className="flex flex-col gap-1">
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-7 w-7"
                                            title="Save caption"
                                            onClick={() => void saveCaption()}
                                            disabled={isSaving}
                                        >
                                            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-7 w-7"
                                            title="Cancel"
                                            onClick={cancelEditing}
                                            disabled={isSaving}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1 min-w-0">
                                    {activePhoto.caption ? (
                                        <p className="text-xs text-muted-foreground truncate">{activePhoto.caption}</p>
                                    ) : (
                                        canWrite ? <p className="text-xs text-muted-foreground/60 italic">No caption</p> : null
                                    )}
                                    {canWrite && onSaveCaption ? (
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-6 w-6 shrink-0"
                                            title="Edit caption"
                                            onClick={startEditing}
                                            data-testid="edit-caption"
                                        >
                                            <Pencil className="h-3 w-3" />
                                        </Button>
                                    ) : null}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            <span className="text-xs text-muted-foreground mr-1">
                                {activeIndex + 1} / {photos.length}
                            </span>
                            <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                title="Download"
                                onClick={() => onDownload(activePhoto)}
                            >
                                <Download className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Media area */}
                    <div className="relative flex items-center justify-center bg-black/80 min-h-[50vh] max-h-[75vh]">
                        {isLoading ? (
                            <Loader2 className="h-6 w-6 animate-spin text-white/70" />
                        ) : error ? (
                            <p className="text-sm text-destructive-foreground px-4 py-8">{error}</p>
                        ) : blobUrl ? (
                            isVideo(activePhoto.mimeType) ? (
                                <video
                                    src={blobUrl}
                                    controls
                                    autoPlay
                                    className="max-h-[75vh] max-w-full object-contain"
                                />
                            ) : (
                                <img
                                    src={blobUrl}
                                    alt={activePhoto.caption || activePhoto.filename}
                                    className="max-h-[75vh] max-w-full object-contain"
                                />
                            )
                        ) : null}

                        {hasPrev && (
                            <Button
                                size="icon"
                                variant="ghost"
                                className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/40 text-white hover:bg-black/60 hover:text-white"
                                title="Previous"
                                onClick={goPrev}
                            >
                                <ChevronLeft className="h-6 w-6" />
                            </Button>
                        )}
                        {hasNext && (
                            <Button
                                size="icon"
                                variant="ghost"
                                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/40 text-white hover:bg-black/60 hover:text-white"
                                title="Next"
                                onClick={goNext}
                            >
                                <ChevronRight className="h-6 w-6" />
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
